import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import MarketingLayout from './MarketingLayout';
import { getPublicAnnouncementDetail } from '../../services/content';
import { ContentItem } from '../../types/content';
import { sanitizeAndNormalizeRichText } from '../../utils/richText';
import { useDocumentMeta } from '../../hooks/useDocumentMeta';

const truncateNavTitle = (title: string, maxLen = 15) => {
  const text = String(title || '');
  return text.length > maxLen ? `${text.slice(0, maxLen)}...` : text;
};

const AnnouncementDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [row, setRow] = useState<ContentItem | null>(null);
  const [prevNav, setPrevNav] = useState<{ id: number; title: string } | null>(null);
  const [nextNav, setNextNav] = useState<{ id: number; title: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const parsedId = Number(id);
      const validId = Number.isInteger(parsedId) && parsedId > 0;
      if (!validId) {
        setRow(null);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const data = await getPublicAnnouncementDetail(parsedId);
        setRow(data.item || null);
        setPrevNav(data.prev || null);
        setNextNav(data.next || null);
      } catch {
        setRow(null);
        setPrevNav(null);
        setNextNav(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  useDocumentMeta({
    title: row ? (row.seo_title || row.title) : undefined,
    description: row ? (row.seo_description || row.summary) : undefined,
    ogImage: row?.image_url,
  });

  return (
    <MarketingLayout>
      <section className="mx-auto max-w-4xl px-6 py-14">
        <div className="mb-4 text-sm">
          <Link to="/announcements" className="text-blue-600 hover:text-blue-700 hover:underline">
            返回列表
          </Link>
        </div>

        {loading ? (
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-gray-600 shadow-sm">加载中...</div>
        ) : row ? (
          <article className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
            <h1 className="text-3xl font-bold text-gray-900">{row.title}</h1>
            {row.created_at ? (
              <p className="mt-2 text-sm text-gray-500">{row.created_at.replace('T', ' ').slice(0, 19)}</p>
            ) : null}
            {row.summary ? <p className="mt-6 text-base leading-8 text-gray-700">{row.summary}</p> : null}
            <div
              className="prose content-rich-text mt-8 max-w-none"
              dangerouslySetInnerHTML={{ __html: sanitizeAndNormalizeRichText(row.body || '') }}
            />
            <div className="mt-8 flex items-center justify-between gap-3 border-t border-gray-100 pt-4">
              {prevNav ? (
                <Link
                  to={`/announcements/${prevNav.id}`}
                  className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                  title={prevNav.title}
                >
                  上一条：{truncateNavTitle(prevNav.title)}
                </Link>
              ) : (
                <span className="cursor-not-allowed text-sm text-gray-400">上一条</span>
              )}
              {nextNav ? (
                <Link
                  to={`/announcements/${nextNav.id}`}
                  className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                  title={nextNav.title}
                >
                  下一条：{truncateNavTitle(nextNav.title)}
                </Link>
              ) : (
                <span className="cursor-not-allowed text-sm text-gray-400">下一条</span>
              )}
            </div>
          </article>
        ) : (
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-gray-500 shadow-sm">
            公告不存在或已下线
          </div>
        )}
      </section>
    </MarketingLayout>
  );
};

export default AnnouncementDetailPage;
